// Direct browser -> UPS Profile API access, plus the same normalization
// logic used by the Backstage backend's profile-linking plugin
// (packages/backend/src/plugins/profile-linking/{types,service}.ts), ported
// verbatim (unchanged logic) so it can run client-side against
// browser-fetched UPS data. Kept in this one file to avoid sprawl.
import type {
  Decision,
  SignalPolarity,
  UiCandidate,
  UiEvidence,
  UiImageEvidence,
  UiProfile,
  UiSignal,
} from './types';

export const UPS_PROFILES_BASE_URL =
  'https://unified-profiles-private.k8s-cobalt.internal.azure.meltwater.io';

// ---- Wire types (mirror backend's types.ts) --------------------------------

export interface SocialNetworkWire {
  network: string;
  id?: string | null;
  handle?: string | null;
  name?: string | null;
  url?: string | null;
  profileImage?: string | null;
  bio?: string | null;
  verified?: boolean | null;
  metrics?: { followerCount?: number | null } | null;
}

export interface ProfileWire {
  rawName?: string | null;
  type?: string | null;
  description?: { text?: string | null } | null;
  socialNetworks?: SocialNetworkWire[] | null;
}

export interface UnifiedProfileWire {
  profile: ProfileWire;
}

export interface BatchProfilesResponseWire {
  ids: Record<string, UnifiedProfileWire>;
  handles: Record<string, UnifiedProfileWire>;
}

export type DiscoveryMethod = 'BIO_REFERENCE' | 'HANDLE_MATCH' | 'IMAGE';
export type ResolutionStatus = 'RESOLVED' | 'NOT_FOUND';
export type MatchType = 'EXACT' | 'LEV1' | 'LEV2' | 'NOT_FOUND';

export interface LinkCandidateWire {
  id: string;
  sourceNetwork: string;
  sourceProfileId: string;
  targetNetwork: string;
  targetProfileId?: string | null;
  discoveryMethod: DiscoveryMethod;
  resolutionStatus: ResolutionStatus;
  latestEvaluationId?: string | null;
}

export interface SignalContributionWire {
  name: string;
  available: boolean;
  matched: boolean;
  contribution: number;
  sourceValue?: string | null;
  targetValue?: string | null;
}

export interface ImageEvidenceWire {
  status: string;
  imageScore?: number | null;
  classification?: 'STRONG' | 'NOT_STRONG' | null;
  reason?: string | null;
  resultClassification?: 'STRONG_MATCH' | 'INCONCLUSIVE' | 'STRONG_MISMATCH' | null;
  resultReason?: string | null;
  imageAdjustment?: number | null;
  classificationVersion?: string | null;
  faceBucket?: string | null;
  bestFaceCosine?: number | null;
  sourceFaceCount?: number | null;
  targetFaceCount?: number | null;
  clipCosine?: number | null;
  clipBucket?: string | null;
  dinoCosine?: number | null;
  dinoBucket?: string | null;
  phashDistance?: number | null;
  phashBucket?: string | null;
  dhashDistance?: number | null;
  dhashBucket?: string | null;
}

export interface EvaluationWire {
  id: string;
  candidateId: string;
  matchType: MatchType;
  levenshteinDistance?: number | null;
  finalScore: number;
  combinedScore?: number | null;
  signals: SignalContributionWire[];
  evaluatedAt: number;
  imageScore?: number | null;
  imageEvidence?: ImageEvidenceWire | null;
}

export interface CandidateDetailWire {
  candidate: LinkCandidateWire;
  evaluations: EvaluationWire[];
}

// ---- Direct browser -> UPS Profile API calls -------------------------------

export class UpsDirectAuthFailedError extends Error {
  readonly code = 'UPS_DIRECT_AUTH_FAILED';
  constructor() {
    super('UPS authorization failed. Re-authorize your Unified Profiles token.');
  }
}

export class UpsDirectNetworkError extends Error {
  readonly code = 'UPS_DIRECT_NETWORK_ERROR';
  constructor() {
    super('Unable to reach Unified Profiles from the browser.');
  }
}

async function upsFetch(path: string, token: string): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(`${UPS_PROFILES_BASE_URL}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    throw new UpsDirectNetworkError();
  }
  if (response.status === 401 || response.status === 403) {
    throw new UpsDirectAuthFailedError();
  }
  return response;
}

export async function getProfileByHandle(
  network: string,
  handle: string,
  token: string,
): Promise<UnifiedProfileWire> {
  const response = await upsFetch(
    `/v1/${encodeURIComponent(network)}/handles/${encodeURIComponent(handle)}`,
    token,
  );
  if (!response.ok) throw new Error(`UPS profile lookup failed (${response.status})`);
  return (await response.json()) as UnifiedProfileWire;
}

export async function getProfileById(
  network: string,
  id: string,
  token: string,
): Promise<UnifiedProfileWire> {
  const response = await upsFetch(
    `/v1/${encodeURIComponent(network)}/ids/${encodeURIComponent(id)}`,
    token,
  );
  if (!response.ok) throw new Error(`UPS profile lookup failed (${response.status})`);
  return (await response.json()) as UnifiedProfileWire;
}

export async function batchGetProfilesByIds(
  network: string,
  ids: string[],
  token: string,
): Promise<BatchProfilesResponseWire> {
  if (ids.length === 0) return { ids: {}, handles: {} };
  let response: Response;
  try {
    response = await fetch(`${UPS_PROFILES_BASE_URL}/v1/${encodeURIComponent(network)}/batch`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ids }),
    });
  } catch {
    throw new UpsDirectNetworkError();
  }
  if (response.status === 401 || response.status === 403) {
    throw new UpsDirectAuthFailedError();
  }
  if (!response.ok) throw new Error(`UPS batch lookup failed (${response.status})`);
  return (await response.json()) as BatchProfilesResponseWire;
}

// ---- Normalization (ported verbatim from the backend plugin's service.ts) --

const REDACTED_SIGNAL_VALUES = new Set(['email']);

export function mapProfileToUiProfile(profile: ProfileWire, network: string): UiProfile | null {
  const socialNetwork = (profile.socialNetworks ?? []).find(sn => sn.network === network);
  if (!socialNetwork) return null;

  return {
    network,
    nativeProfileId: socialNetwork.id ?? null,
    handle: socialNetwork.handle ?? null,
    displayName: socialNetwork.name ?? profile.rawName ?? null,
    bio: socialNetwork.bio ?? profile.description?.text ?? null,
    followerCount: socialNetwork.metrics?.followerCount ?? null,
    imageUrl: socialNetwork.profileImage ?? null,
    verified: socialNetwork.verified ?? null,
    accountType: profile.type ?? null,
  };
}

export function selectEffectiveEvaluation(
  candidate: LinkCandidateWire,
  evaluations: EvaluationWire[],
): EvaluationWire | null {
  if (evaluations.length === 0) return null;

  if (candidate.latestEvaluationId) {
    const matched = evaluations.find(e => e.id === candidate.latestEvaluationId);
    if (matched) return matched;
  }

  return [...evaluations].sort((a, b) => b.evaluatedAt - a.evaluatedAt)[0];
}

export function normalizeImageEvidence(
  wire: ImageEvidenceWire | null | undefined,
): UiImageEvidence {
  if (!wire) {
    return { decision: 'UNKNOWN', status: null, reason: null, imageScore: null, imageAdjustment: null };
  }

  let decision: Decision;
  let reason: string | null;
  if (wire.resultClassification) {
    decision = wire.resultClassification;
    reason = wire.resultReason ?? null;
  } else if (wire.classification) {
    decision = wire.classification === 'STRONG' ? 'LEGACY_STRONG_SIGNAL' : 'LEGACY_NO_STRONG_SIGNAL';
    reason = wire.reason ?? null;
  } else {
    decision = 'UNKNOWN';
    reason = null;
  }

  return {
    decision,
    status: wire.status ?? null,
    reason,
    imageScore: wire.imageScore ?? null,
    imageAdjustment: wire.imageAdjustment ?? null,
    classificationVersion: wire.classificationVersion ?? undefined,
    faceBucket: wire.faceBucket ?? undefined,
    bestFaceCosine: wire.bestFaceCosine ?? undefined,
    sourceFaceCount: wire.sourceFaceCount ?? undefined,
    targetFaceCount: wire.targetFaceCount ?? undefined,
    clipCosine: wire.clipCosine ?? undefined,
    clipBucket: wire.clipBucket ?? undefined,
    dinoCosine: wire.dinoCosine ?? undefined,
    dinoBucket: wire.dinoBucket ?? undefined,
    phashDistance: wire.phashDistance ?? undefined,
    phashBucket: wire.phashBucket ?? undefined,
    dhashDistance: wire.dhashDistance ?? undefined,
    dhashBucket: wire.dhashBucket ?? undefined,
  };
}

function polarityFor(signal: SignalContributionWire): SignalPolarity {
  if (!signal.available) return 'neutral';
  if (signal.contribution > 0) return 'supports';
  if (signal.contribution < 0) return 'contradicts';
  return 'neutral';
}

export function normalizeSignals(signals: SignalContributionWire[]): UiSignal[] {
  return signals.map(signal => {
    const redact = REDACTED_SIGNAL_VALUES.has(signal.name);
    return {
      name: signal.name,
      available: signal.available,
      matched: signal.matched,
      contribution: signal.contribution,
      polarity: polarityFor(signal),
      sourceValue: redact ? null : signal.sourceValue ?? null,
      targetValue: redact ? null : signal.targetValue ?? null,
    };
  });
}

export function topExplanationSignals(signals: UiSignal[], limit = 3): UiSignal[] {
  return [...signals]
    .filter(s => s.available)
    .sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution))
    .slice(0, limit);
}

export function buildUiEvidence(evaluation: EvaluationWire): UiEvidence {
  const signals = normalizeSignals(evaluation.signals ?? []);
  return {
    evaluationId: evaluation.id,
    matchType: evaluation.matchType,
    levenshteinDistance: evaluation.levenshteinDistance ?? null,
    metadataScore: evaluation.finalScore,
    combinedScore: evaluation.combinedScore ?? null,
    evaluatedAt: evaluation.evaluatedAt,
    signals,
    imageEvidence: normalizeImageEvidence(evaluation.imageEvidence),
  };
}

export function buildUiCandidate(
  candidate: LinkCandidateWire,
  evaluation: EvaluationWire | null,
  targetProfile: UiProfile | null,
): UiCandidate {
  const evidence = evaluation ? buildUiEvidence(evaluation) : null;
  return {
    id: candidate.id,
    profile: targetProfile,
    discoveryMethod: candidate.discoveryMethod,
    resolutionStatus: candidate.resolutionStatus,
    decision: evidence?.imageEvidence.decision ?? 'UNKNOWN',
    explanationSignals: evidence ? topExplanationSignals(evidence.signals) : [],
    evidence,
  };
}
